import React, { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { JsonLd } from '../../utils/structuredData';
import { trackEvent } from '../../utils/analytics';
import { useInMarketingShell } from './MarketingContext';
import '../AIEmployees/AIEmployeesLandingPage.css';
import './BookDemoPage.css';

/**
 * BookDemoPage: /book-a-demo (CEL-3640, every decision ruled by Nitish Oct 22 2026).
 * The destination of the Book a demo button in the Home + /ai-employees closing section (header pill until Nov 1 2026): a short
 * job-first intro, four cards, and Nitish's "CellCog Demo" Google appointment schedule
 * embedded so a visitor books without leaving cellcog.ai. Phone order (<=760px, CSS
 * `order`): H1, sub, calendar, cards, links; the cards exist once in the DOM.
 *
 * The iframe is left out of the react-snap snapshot (ReactSnap UA, same idiom as
 * HomeHeroVideo). index.js uses createRoot (not hydrate), so a prerendered iframe would
 * load Google's page and then be replaced and loaded again when React mounts; the
 * prerender carries a same-height white slot instead.
 * Google's frame stays white on the dark face (it cannot be restyled) and tells this
 * page nothing about bookings: bookings are counted from Nitish's calendar.
 *
 * Theme: the .aie-landing token system, same face logic as SecurityPage. Events are
 * `cta_click` with cta_name demo_calendar_loaded (iframe onLoad, once per view),
 * demo_open_new_tab, demo_get_started (TRACKING.md "Book a demo").
 * Registrations: App.js route, adaptivePaths.js, seo.py sitemap, react-snap include,
 * public/book-a-demo/index.md, public/llms.txt; LB URL map (Arjun, CEL-3655).
 */

const PAGE_PATH = '/book-a-demo';
const CALENDAR_EMBED_SRC =
  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ1_WbXVbqxc3LNB6niJkvU-Gr6lUZ2ExNU9w9uTlQLMW7SsnB2CSLbu6FpGXSXePWnjbWc4uCjm?gv=true';
const CALENDAR_LINK = 'https://calendar.app.google/Zu4dXQ8UnK2x4nds9';

const TITLE = 'Book a demo | CellCog';
const DESCRIPTION =
  "Book a call with CellCog. See what our AI employees can do, tell us the job you want off your plate, and we'll help you get started.";

const iconProps = {
  viewBox: '0 0 24 24',
  width: 17,
  height: 17,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: 'false',
};

const CARDS = [
  {
    title: 'See them at work',
    body: "A live look at what CellCog's AI employees can do.",
    icon: (
      <svg {...iconProps}>
        <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
  {
    title: 'Bring any job',
    body: 'Describe the work you want off your plate. No fixed list of roles.',
    icon: (
      <svg {...iconProps}>
        <rect x="3" y="7" width="18" height="13" rx="2" />
        <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      </svg>
    ),
  },
  {
    title: 'Hire your first one',
    body: 'We help you set up the right AI employee for your job.',
    icon: (
      <svg {...iconProps}>
        <circle cx="9" cy="8" r="4" />
        <path d="M2 21a7 7 0 0 1 14 0" />
        <path d="M19 8v6M16 11h6" />
      </svg>
    ),
  },
  {
    title: 'Honest fit check',
    body: "If CellCog isn't right for you, we'll say so.",
    icon: (
      <svg {...iconProps}>
        <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
];

const isPrerender = () =>
  typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);

const track = (ctaName, location) =>
  trackEvent('cta_click', { cta_name: ctaName, page: PAGE_PATH, location });

const BookDemoPage = () => {
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();
  const dark = inMarketingShell ? true : isDarkMode;
  const canonicalUrl = `${SEO_DEFAULTS.url}${PAGE_PATH}`;

  // onLoad also fires on navigations INSIDE Google's frame; count the first load only.
  const loadTracked = useRef(false);
  const onCalendarLoad = () => {
    if (loadTracked.current) return;
    loadTracked.current = true;
    track('demo_calendar_loaded', 'calendar');
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: TITLE,
          description: DESCRIPTION,
          url: canonicalUrl,
          isPartOf: { '@type': 'WebSite', name: SEO_DEFAULTS.siteName, url: SEO_DEFAULTS.url },
          dateModified: '2026-10-22',
        }}
      />

      <div className={`aie-landing bkd-page ${dark ? 'dark' : 'light'}`}>
        <div className="bkd__inner">
          <h1 className="bkd__title aie-display">Book a demo</h1>
          <p className="bkd__sub bkd__sub--desk">
            See CellCog's AI employees at work, and get started with yours.
          </p>
          <p className="bkd__sub bkd__sub--phone">
            See CellCog's AI employees at work. Tell us the job you want off your
            plate, and we'll help you get started.
          </p>

          <ul className="bkd__cards">
            {CARDS.map((c) => (
              <li key={c.title} className="bkd__card">
                <span className="bkd__icon">{c.icon}</span>
                <b className="bkd__card-title">{c.title}</b>
                <span className="bkd__card-body">{c.body}</span>
              </li>
            ))}
          </ul>

          <div className="bkd__cal">
            <div className="bkd__ribbon">
              <span className="bkd__ribbon-full">
                Your call is with <b>Nitish Garg</b>, Founder &amp; CEO of CellCog
              </span>
              <span className="bkd__ribbon-short">
                Your call with <b>Nitish Garg</b>, CEO
              </span>
            </div>
            {!isPrerender() ? (
              <iframe
                className="bkd__frame"
                title="Book a CellCog demo"
                src={CALENDAR_EMBED_SRC}
                loading="eager"
                onLoad={onCalendarLoad}
              />
            ) : (
              <div className="bkd__frame bkd__frame--slot" aria-hidden="true" />
            )}
          </div>

          <div className="bkd__after">
            <p className="bkd__alt">
              Rather start on your own?{' '}
              <Link
                to="/signup"
                className="bkd__alt-link"
                onClick={() => track('demo_get_started', 'below_calendar')}
              >
                Get started
              </Link>
            </p>
            <p className="bkd__note">
              Calendar not loading?{' '}
              <a
                href={CALENDAR_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="bkd__note-link"
                onClick={() => track('demo_open_new_tab', 'below_calendar')}
              >
                Open it in a new tab
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookDemoPage;
